import type { Block } from "../contract/types";
import type { BlockOf, BlockType } from "../blocks/registry";

/**
 * Lectura defensiva de `PresetRef.data`.
 *
 * El contrato no valida `data` —es un diccionario libre— así que todo lo que
 * salga de ahí es dato por confirmar. Estos lectores devuelven `null` o `[]`
 * cuando la clave no está o viene con otro tipo, y cada preset decide qué
 * hacer con lo que falta en vez de reventar.
 */

export type PresetData = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function asRecord(value: unknown): PresetData | null {
  return isRecord(value) ? value : null;
}

export function readString(data: PresetData, key: string): string | null {
  const value = data[key];
  if (typeof value === "string" && value.trim() !== "") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

export function readNumber(data: PresetData, key: string): number | null {
  const value = data[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function readArray(data: PresetData, key: string): unknown[] {
  const value = data[key];
  return Array.isArray(value) ? value : [];
}

export function readStringArray(data: PresetData, key: string): string[] {
  return readArray(data, key)
    .map((item) => (typeof item === "string" ? item : typeof item === "number" ? String(item) : null))
    .filter((item): item is string => item !== null && item.trim() !== "");
}

export function readObjects(data: PresetData, key: string): PresetData[] {
  return readArray(data, key).filter(isRecord);
}

export function readRecord(data: PresetData, key: string): PresetData | null {
  const value = data[key];
  return isRecord(value) ? value : null;
}

/** Normaliza un estado de módulo/hito a los tres valores del contrato. */
export function readStatus(
  data: PresetData,
  key = "status",
): "done" | "current" | "upcoming" {
  const value = readString(data, key);
  if (value === "done" || value === "current" || value === "upcoming") return value;
  return "upcoming";
}

const PREFIX_BY_CODE: Record<string, string> = {
  cl: "CL",
  ea: "EA",
  tf: "TF",
};

export const RUBRIC_LABELS: Record<string, { code: string; label: string; modules: string[] }> = {
  CL: { code: "CL", label: "Controles de lectura", modules: [] },
  EA: { code: "EA", label: "Evaluaciones parciales", modules: [] },
  TF: { code: "TF", label: "Trabajo final", modules: [] },
};

/**
 * Convierte `assessment: {cl: 0.20, ea: 0.45, tf: 0.35}` en componentes de
 * rúbrica. Los pesos del curso son fracciones y así se quedan: la conversión a
 * porcentaje la hace el átomo `rubric` al pintar.
 */
export function assessmentComponents(
  assessment: PresetData | null,
): { code: string; label: string; weight: number; modules?: string[] }[] {
  if (!assessment) return [];
  const components: { code: string; label: string; weight: number; modules?: string[] }[] = [];
  for (const [key, meta] of Object.entries(RUBRIC_LABELS)) {
    const weight = readNumber(assessment, key.toLowerCase());
    if (weight === null) continue;
    const modules = readStringArray(assessment, `${key.toLowerCase()}_modules`);
    components.push({
      code: PREFIX_BY_CODE[key.toLowerCase()] ?? key.toUpperCase(),
      label: meta.label,
      weight,
      ...(modules.length > 0 ? { modules } : {}),
    });
  }
  return components;
}

/**
 * Crea un bloque sintético con un `id` estable dentro del preset.
 *
 * El único punto donde el tipado se relaja: TypeScript no puede demostrar que
 * el objeto construido con un `type` genérico corresponde a ese miembro de la
 * unión, aunque el `Omit` de entrada ya obligue a pasar las props correctas.
 */
let atomCounter = 0;

export function createAtom<T extends BlockType>(
  type: T,
  props: Omit<BlockOf<T>, "id" | "type">,
): BlockOf<T> {
  atomCounter += 1;
  return { ...props, id: `preset_${type}_${atomCounter}`, type } as BlockOf<T>;
}

/** Bloques sin contenido no se pintan: un hueco vacío no es información. */
export function compactBlocks(blocks: (Block | null)[]): Block[] {
  return blocks.filter((block): block is Block => block !== null);
}
